import { useState } from 'react';
import { STRINGS } from '@/strings/strings';
import { removeCastEntry, swapCastPhoto } from '../../api';
import { usePhotoUpload } from '../../use-photo-upload';
import type { PreviewEnvelope } from '../../types';
import { FotoUploadSlot } from '../personen/FotoUploadSlot';
import type { FotoUploadSlotState } from '../personen/FotoUploadSlot';

/**
 * 2026-10-14 — per-figure block-derivation rejection screen.
 *
 * Rendered by BookRoute when `envelope.status === 'block_derivation_rejected'`.
 * The server has already landed one or more rejections at
 * `envelope.intake.derived.rejections` (plural, keyed by cast key).
 * This screen lists each REJECTED figure with its approved hint text
 * and a photo-swap affordance (reuses `FotoUploadSlot` + `swapCastPhoto`
 * — same primitives the sister `PhotoRejectedScreen` uses, Sebastian
 * ruling 2026-08-12).
 *
 * Approved copy per `STRINGS.book.blockDerivationRejected.*`:
 *   heading   — fixed, no interpolation.
 *   tierBody  — `[[name]]` → figure's name. Reason='not_an_animal'.
 *   personBody — `[[name]]` → figure's name. Reason='photo_unusable'.
 *
 * Two affordances per rejected figure (Sebastian ruling 2026-10-14):
 *   - "Weiter" (primary) uploads + swaps the photo via the existing
 *     `swapCastPhoto` endpoint. Maps to the "nimm ein anderes Foto"
 *     instruction in both texts.
 *   - "Entfernen" (secondary) removes the entire cast entry via the
 *     new `removeCastEntry` endpoint. Hidden when `cast.length === 1`
 *     because the server refuses to leave the order with zero figures
 *     (CreateBookOrderCastEntryDto.cast @ArrayMinSize(1)). Maps to the
 *     "Entferne [[name]] hier" instruction in the tier text.
 *
 * Both actions refetch the envelope on success; BookRoute re-dispatches
 * (if other rejections remain, the backend flips back to
 * block_derivation_rejected with a shrunk rejections map and the
 * customer sees the next failed figure).
 */
const BUTTON_GOLD =
  'h-12 w-full rounded-lg bg-dream-gold px-8 text-base font-bold text-dream-navy hover:bg-dream-gold/90 disabled:opacity-60';
// Underline-secondary style verbatim from the sibling PhotoRejectedScreen
// (`deleteFullBodyCta` branch) — same visual treatment the existing
// "Foto entfernen" button uses so the Entfernen affordance here reads
// as a secondary action under the gold primary.
const BUTTON_SECONDARY_UNDERLINE =
  'w-full text-center text-sm text-dream-navy underline disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-dream-navy/50';
const ERROR_CLASS = 'text-sm text-error';

export interface BlockDerivationRejectedScreenProps {
  readonly envelope: PreviewEnvelope;
  readonly onRefetch: () => void;
}

interface PickedFile {
  readonly file: File;
  readonly previewUrl: string;
}

type Phase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'uploading' }
  | { readonly kind: 'removing' }
  | { readonly kind: 'error' }
  | { readonly kind: 'remove-error' };

interface RejectedFigure {
  readonly key: string;
  readonly name: string;
  readonly personType: 'person' | 'tier';
  readonly reason: 'not_an_animal' | 'photo_unusable';
}

/**
 * Read the rejected figures from the typed envelope projections:
 *   envelope.intake.derived.rejections  — Record<castKey, {reason}>
 *   envelope.intake.cast                — readonly {key, name, personType}[]
 *
 * Both projections land together from the backend's resolveIntake
 * Projection (Part 5 fix, 2026-10-14). Falls back to the castKey +
 * personType='person' only in the hypothetical case where the backend
 * dropped a shape (defensive — the real envelope always carries
 * both when status === 'block_derivation_rejected').
 */
function readRejectedFigures(envelope: PreviewEnvelope): readonly RejectedFigure[] {
  const rejections = envelope.intake?.derived?.rejections;
  if (rejections === undefined) return [];
  const castByKey = new Map<string, { readonly name: string; readonly personType: 'person' | 'tier' }>();
  for (const entry of envelope.intake?.cast ?? []) {
    castByKey.set(entry.key, { name: entry.name, personType: entry.personType });
  }
  const result: RejectedFigure[] = [];
  for (const [castKey, entry] of Object.entries(rejections)) {
    const match = castByKey.get(castKey);
    result.push({
      key: castKey,
      name: match && match.name.length > 0 ? match.name : castKey,
      personType: match?.personType ?? 'person',
      reason: entry.reason,
    });
  }
  return result;
}

function pickBody(reason: 'not_an_animal' | 'photo_unusable', name: string): string {
  const template =
    reason === 'not_an_animal'
      ? STRINGS.book.blockDerivationRejected.tierBody
      : STRINGS.book.blockDerivationRejected.personBody;
  return template.replaceAll('[[name]]', name);
}

export function BlockDerivationRejectedScreen({
  envelope,
  onRefetch,
}: BlockDerivationRejectedScreenProps) {
  const figures = readRejectedFigures(envelope);
  // Server refuses to remove the last cast entry (CreateBookOrderCast
  // EntryDto.cast @ArrayMinSize(1)). Hide the Entfernen affordance
  // client-side so the customer never clicks a button that would 409.
  // Reads from the typed `envelope.intake.cast` projection (Part 5
  // fix); defect 2 pre-fix read from a path that was never projected
  // and the length was always 0.
  const castLength = envelope.intake?.cast?.length ?? 0;
  const canRemove = castLength > 1;
  return (
    <div className="space-y-6 py-6">
      <h1 className="text-h3 leading-tight text-dream-navy">
        {STRINGS.book.blockDerivationRejected.heading}
      </h1>
      {figures.map((f) => (
        <FigureRow
          key={f.key}
          bookOrderId={envelope.bookOrderId}
          castKey={f.key}
          body={pickBody(f.reason, f.name)}
          personType={f.personType}
          canRemove={canRemove}
          onRefetch={onRefetch}
        />
      ))}
    </div>
  );
}

interface FigureRowProps {
  readonly bookOrderId: string;
  readonly castKey: string;
  readonly body: string;
  readonly personType: 'person' | 'tier';
  readonly canRemove: boolean;
  readonly onRefetch: () => void;
}

function FigureRow({
  bookOrderId,
  castKey,
  body,
  personType,
  canRemove,
  onRefetch,
}: FigureRowProps) {
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [picked, setPicked] = useState<PickedFile | null>(null);
  const photoUpload = usePhotoUpload();

  const slotState: FotoUploadSlotState =
    picked === null ? { kind: 'empty' } : { kind: 'picked', previewUrl: picked.previewUrl };

  const handleFileSelected = (file: File) => {
    if (picked !== null) URL.revokeObjectURL(picked.previewUrl);
    setPicked({ file, previewUrl: URL.createObjectURL(file) });
  };

  const handleRemove = () => {
    if (picked !== null) URL.revokeObjectURL(picked.previewUrl);
    setPicked(null);
    setPhase({ kind: 'idle' });
  };

  const runSwap = async () => {
    if (picked === null) return;
    setPhase({ kind: 'uploading' });
    try {
      const [key] = await photoUpload.upload([picked.file]);
      if (key === undefined) {
        throw new Error('block-derivation-rejected: upload returned no key');
      }
      await swapCastPhoto(bookOrderId, castKey, {
        whichPhoto: 'face',
        newPhotoKey: key,
      });
      onRefetch();
    } catch (err) {
      setPhase({ kind: 'error' });
      // eslint-disable-next-line no-console
      console.warn('[block-derivation-rejected] swap failed', err);
    }
  };

  const runRemove = async () => {
    if (!canRemove) return;
    setPhase({ kind: 'removing' });
    try {
      await removeCastEntry(bookOrderId, castKey);
      onRefetch();
    } catch (err) {
      setPhase({ kind: 'remove-error' });
      // eslint-disable-next-line no-console
      console.warn('[block-derivation-rejected] remove failed', err);
    }
  };

  const inFlight = phase.kind === 'uploading' || phase.kind === 'removing';

  return (
    <div className="space-y-3 rounded-lg border border-dream-navy/10 bg-dream-background p-4">
      <p className="text-base leading-relaxed text-dream-navy">{body}</p>
      <FotoUploadSlot
        state={slotState}
        onFileSelected={handleFileSelected}
        onRemove={handleRemove}
        // Defect 4 fix (Sebastian ruling 2026-10-14): the four
        // STRINGS.book.personen.fotoRules.* lines are person-oriented
        // ("Eine Person pro Bild", "Gesicht frei und gut erkennbar",
        // "keine Sonnenbrille, keine Kappe", "Alltagsfotos sind
        // besser als Studiofotos"). No animal-photo hint strings
        // exist in strings.ts today, so for tier figures we hide the
        // list rather than invent new copy.
        showRules={personType === 'person'}
        ariaLabel={STRINGS.book.photoRejected.slotAriaEmpty}
      />
      {phase.kind === 'uploading' && (
        <p className="text-sm text-dream-navy/70">{STRINGS.book.photoRejected.uploadingHeading}</p>
      )}
      {phase.kind === 'error' && (
        <p className={ERROR_CLASS}>{STRINGS.book.photoRejected.swapErrorPlaceholder}</p>
      )}
      {phase.kind === 'remove-error' && (
        <p className={ERROR_CLASS}>{STRINGS.book.photoRejected.swapErrorPlaceholder}</p>
      )}
      <button
        type="button"
        onClick={() => void runSwap()}
        disabled={picked === null || inFlight}
        className={BUTTON_GOLD}
      >
        {phase.kind === 'error'
          ? STRINGS.book.photoRejected.retryCta
          : STRINGS.book.personen.blockWeiterLabel}
      </button>
      {canRemove && (
        <button
          type="button"
          onClick={() => void runRemove()}
          disabled={inFlight}
          className={BUTTON_SECONDARY_UNDERLINE}
          data-testid="block-derivation-rejected-remove"
        >
          {STRINGS.book.blockDerivationRejected.removeCta}
        </button>
      )}
    </div>
  );
}
